import { useCallback, useEffect, useRef, useState } from 'react';

export interface Resource<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (next: T | ((prev: T | null) => T)) => void;
}

/**
 * One independently loading request. Each panel section owns its own, so a
 * failing call shows an error + retry in that section only. Responses from a
 * superseded load (key changed, or reloaded) are dropped.
 */
export function useResource<T>(load: (() => Promise<T>) | null, key: string): Resource<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(Boolean(load));
  const seq = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    const fn = loadRef.current;
    const mine = ++seq.current;

    if (!fn) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fn();

      if (mine === seq.current) {
        setDataState(result);
      }
    } catch (err) {
      if (mine === seq.current) {
        console.error(err);
        setError(err);
      }
    } finally {
      if (mine === seq.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    setDataState(null);
    void reload();
  }, [key, reload]);

  const setData = useCallback((next: T | ((prev: T | null) => T)) => {
    setDataState((prev) => (typeof next === 'function' ? (next as (p: T | null) => T)(prev) : next));
  }, []);

  return { data, error, loading, reload, setData };
}
